import { FC, ChangeEvent, FocusEvent, useRef, useState } from "react";
import styled from "styled-components";
import { CreateFormTheme } from "../../utils/themes";
import { useTheme } from "../../middleware/ThemeContext";

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const StyledInput = styled('input')<{
  $hasError?: boolean;
  width?: string;
  $hideNativePicker?: boolean;
}>(({ theme, $hasError, width, $hideNativePicker }) => ({
  padding: '8px 8px',
  paddingRight: '40px', // space for icon
  width: width || '100%',
  color: theme.CreateFormTheme.colors.headerColor,
  fontWeight: 500,
  height: '42px',
  fontSize: '14px',
  border: `1px solid ${$hasError
    ? theme.CreateFormTheme.colors.error
    : theme.CreateFormTheme.colors.inputBorders
    }`,
  borderRadius: CreateFormTheme.borderRadius,
  fontFamily: '"Raleway", sans-serif',
  backgroundColor: theme.CreateFormTheme.colors.inputBackground,

  // Caret (blinking vertical line) color
  caretColor: theme.CreateFormTheme.colors.text,

  cursor: 'text',

  '&:focus': {
    outline: `2px solid ${theme.CreateFormTheme.colors.border}`,
    cursor: 'text',
  },

  '::placeholder': {
    color: theme.CreateFormTheme.colors.placeHolder,
    opacity: 1,
  },
  // remove native browser date/time picker icons so we can show a custom icon
  appearance: 'none',
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  // If caller requests to hide native picker (and provide custom icon), remove the calendar indicator and background-image
  // Target pseudo-elements on the input itself using & so styled-components emits selectors for the element
  '&::-webkit-calendar-picker-indicator': $hideNativePicker
    ? { display: 'none', WebkitAppearance: 'none' }
    : { WebkitAppearance: 'none' },
  // also clear background-image which some browsers use to show native icon
  backgroundImage: $hideNativePicker ? 'none' : undefined,
  // hide clear/spin buttons on webkit browsers
  '&::-webkit-clear-button': $hideNativePicker ? { display: 'none' } : undefined,
  '&::-webkit-inner-spin-button': $hideNativePicker ? { display: 'none' } : undefined,
  // Edge/IE clear
  '::-ms-clear': {
    display: 'none',
  },
  // Specifically target date/time input pseudo elements using attribute selector on the element
  '&[type="date"]::-webkit-calendar-picker-indicator': $hideNativePicker
    ? { display: 'none', WebkitAppearance: 'none' }
    : { WebkitAppearance: 'none' },
  '&[type="time"]::-webkit-calendar-picker-indicator': $hideNativePicker
    ? { display: 'none', WebkitAppearance: 'none' }
    : { WebkitAppearance: 'none' },
  '&[type="datetime-local"]::-webkit-calendar-picker-indicator': $hideNativePicker
    ? { display: 'none', WebkitAppearance: 'none' }
    : { WebkitAppearance: 'none' },
  // For safety, remove background on the input for those types as well
  '&[type="date"], &[type="time"], &[type="datetime-local"], &[type="month"]': $hideNativePicker
    ? { backgroundImage: 'none', backgroundColor: theme.CreateFormTheme.colors.inputBackground }
    : undefined,
  // Firefox: hide the clear and drop-down buttons
  '::-moz-focus-inner': {
    border: 0,
  },

  '&:disabled': {
    // backgroundColor: '#f1f1f1',
    cursor: 'not-allowed',
    color: theme.CreateFormTheme.colors.placeHolder,
    opacity: 0.7
  },
}));

const IconWrapper = styled('span')(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  right: '12px',
  transform: 'translateY(-50%)',
  color: theme.CreateFormTheme.colors.placeHolder,
  fontSize: '18px',
  cursor: 'pointer',
  zIndex: 11,
}));

// Visual mask to cover Firefox's native date/time icon area so only the custom icon is visible
const NativeIconMask = styled('span')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  right: 0,
  height: '90%',
  width: '70px',
  margin: '1px',
  backgroundColor: theme.CreateFormTheme.colors.inputBackground,
  borderTopRightRadius: CreateFormTheme.borderRadius,
  borderBottomRightRadius: CreateFormTheme.borderRadius,
  zIndex: 10,
  pointerEvents: 'none',
}));

interface CustomInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  pattern?: string;
  error?: string;
  className?: string;
  width?: string;
  min?: number | string; // Allow both number and string for date inputs
  max?: number | string; // Allow both number and string for date inputs
  disabled?: boolean;
  rightIcon?: React.ReactNode;
  type?: string;
  minLength?: number;
  maxLength?: number;
  onBlur?: (event?: React.FocusEvent<HTMLInputElement>) => void;
  validate?: (value: string) => string | null; // Custom validation function
  step?: string;
  style?: React.CSSProperties;
  forPreview?: boolean;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
}
export const ErrorText = styled.div`
  color: ${CreateFormTheme.colors.error};
  font-size: 12px;
  // margin-top: 4px;
  // position: absolute;
  // bottom: -18px;
  left: 0;
`;

const CustomInput: FC<CustomInputProps> = ({
  value,
  onChange,
  placeholder,
  error,
  pattern,
  className,
  required,
  width,
  min,
  max,
  disabled = false,
  rightIcon,
  type = "text",
  minLength,
  maxLength,
  onBlur,
  validate,
  step,
  style,
  forPreview = false,
  onKeyDown
}) => {
  const { theme } = useTheme();
  const inputRef = useRef<HTMLInputElement>(null);
  const [touched, setTouched] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const isFirefox = typeof navigator !== 'undefined' && /firefox/i.test(navigator.userAgent);
  const isDateLikeType = ["date", "time", "datetime-local", "datetime", "month"].includes(type);

  // Combine passed error and local validation error
  const finalError = error || localError;

  const handleIconClick = () => {
    const inputEl = inputRef.current;
    if (
      ["date", "time", "datetime-local", "datetime", "month"].includes(type) &&
      inputEl &&
      !inputEl.disabled &&
      !inputEl.readOnly
    ) {
      inputEl.showPicker?.();
    }
  };

  const validateInput = (value: string): string | null => {

    const formatDateForMessage = (dateValue: string | number): string => {
      const parsedDate = new Date(dateValue);
      if (Number.isNaN(parsedDate.getTime())) {
        return String(dateValue);
      }

      const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
      const day = String(parsedDate.getDate()).padStart(2, "0");
      const year = parsedDate.getFullYear();
      return `${month}-${day}-${year}`;
    };

    if (validate) {
      const customError = validate(value);
      if (customError) return customError;
    }

    if (required && !value.trim()) {
      return "This field is required";
    }

    if (pattern && value && !new RegExp(pattern).test(value)) {
      return "Invalid format";
    }

    if (minLength !== undefined && value.length < minLength) {
      return `Minimum ${minLength} characters required`;
    }

    if (maxLength !== undefined && value.length > maxLength) {
      return `Maximum ${maxLength} characters allowed`;
    }

    if (min !== undefined) {
      if (type === "number" && Number(value) < Number(min)) {
        return `Minimum value is ${min}`;
      }
      if (["date", "month", "time"].includes(type) && value < String(min)) {
        return `Date must be after ${formatDateForMessage(String(min))}`;
      }
    }

    if (max !== undefined) {
      if (type === "number" && Number(value) > Number(max)) {
        return `Maximum value is ${max}`;
      }
      if (["date", "month", "time"].includes(type) && value > String(max)) {
        return `Date must be before ${formatDateForMessage(String(max))}`;
      }
    }

    return null;
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);

    // Validate on change if already touched
    if (touched && !forPreview) {
      setLocalError(validateInput(newValue));
    }
  };

  const handleBlur = (event?: FocusEvent<HTMLInputElement>) => {
    if (!forPreview) {
      setTouched(true);
      const currentValue = event?.target?.value ?? value;
      setLocalError(validateInput(currentValue));
      if (onBlur) onBlur(event);
    }
  };  

  return (
    <>
      <InputWrapper>
        <StyledInput
          style={style}
          theme={theme}
          ref={inputRef}
          className={className}
          width={width}
          value={value}
          type={type}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          $hasError={!!finalError}
          $hideNativePicker={!!(rightIcon && ["date", "time", "datetime-local", "datetime", "month"].includes(type))}
          required={required}
          pattern={pattern}
          min={min}
          max={max}
          minLength={minLength}
          maxLength={maxLength}
          disabled={disabled}
          step={step}
          onKeyDown={onKeyDown}
        />
        {isFirefox && isDateLikeType && rightIcon && (
          <NativeIconMask theme={theme} />
        )}
        {["date", "time", "datetime-local", "datetime", "month"].includes(type) && rightIcon && (
          <IconWrapper theme={theme} onMouseDown={handleIconClick}>
            {rightIcon}
          </IconWrapper>
        )}
      </InputWrapper>
      {finalError && <ErrorText>{finalError}</ErrorText>}
    </>
  );
};

export default CustomInput;